import {
  eccnBase,
  licencePostureOf,
  primaryControlOf,
  controlReasonsOf,
  reasonPhrase,
} from 'components/ExportDesign/exportDesignControl';
import { humanizeKey, humanizeSpecRef } from 'components/ExportDesign/exportDesignProgress';
import { LICENSING_IMPLICATION } from 'components/ExportDesign/ExportDesignTicket';
import { sanitizeModelText } from 'utils/sanitizeModelText';

// --- The result card's data (UX slice S2). The backend sends it as `data.result_card` on every answer
// that presents a classification (export_design_workflow._result_card); the markdown answer is still
// sent alongside it and stays the fallback, the saved chat's text and the ticket's record.
//
// resultCardFromState MIRRORS _result_card for a chat reopened from a saved ticket whose stored card was
// dropped to fit (exportDesignChatTranscript's size steps): change one, change the other. Everything is
// read defensively, because a stored chat is data, not trusted.

export type ResultCardStatus = 'preliminary' | 'checked' | 'provisional' | 'revised' | 'not_reviewed';
export type Regime = 'ITAR' | 'EAR-listed' | 'EAR99' | 'uncertain';

export interface ResultCardConstraint {
  citation?: string | null;
  control_text?: string | null;
  control_type?: string | null;
  reason_for_control?: string | null;
  matched_spec?: string | null;
  explanation?: string | null;
  decontrol_threshold?: string | null;
  /** S10: the user's own words that trip this control (highlighted only where found verbatim). */
  evidence_quotes?: string[];
}

/** S10: a spec close to a control's limit without tripping it ("close to a limit", board 04). */
export interface ResultCardNearMiss {
  spec?: string | null;
  citation?: string | null;
  note?: string | null;
}

/** The card's near miss, tied to a spec-sheet key when it names one. */
export interface CardNearMiss {
  spec_key: string | null;
  label: string;
  citation: string;
  note: string;
}

/** The user message holding the evidence quotes, and the quotes as they appear in it (S10). */
export interface CardEvidence {
  source: string;
  quotes: string[];
}

export interface ResultCardCaveat {
  missing_spec?: string | null;
  assumed?: string | null;
  impact?: string | null;
}

export interface ResultCardClassification {
  current_regime?: string | null;
  candidate_usml?: string[];
  candidate_eccns?: string[];
  binding_constraints?: ResultCardConstraint[];
  caveats?: ResultCardCaveat[];
  matched_user_claim?: boolean | null;
  order_of_review_note?: string | null;
  explanation?: string | null;
  confidence?: string | null;
  near_misses?: ResultCardNearMiss[];
}

export interface ResultCardVerifier {
  supported?: boolean;
  unstated_spec_dependency?: boolean;
  missing_specs?: string[];
  issues?: string[];
}

export interface ResultCardSpec {
  key: string;
  label: string;
  value: string;
}

export type ItarScreenOutcome = 'on_usml' | 'not_on_usml' | 'unclear';

/** The classifier's early ITAR answer (UX slice S5, `data.itar_screen` / `state.itar_screen`). */
export interface ItarScreen {
  outcome: ItarScreenOutcome;
  category?: string | null;
  paragraph?: string | null;
  reasons: string[];
  closest_checked: string[];
  open_question?: string | null;
}

/** The screen as the final card shows it: `revised` says what changed when the result disagrees. */
export interface CardItarScreen extends ItarScreen {
  revised?: string | null;
}

export interface ResultCardPayload {
  status: ResultCardStatus;
  classification: ResultCardClassification;
  verifier?: ResultCardVerifier | null;
  specs?: ResultCardSpec[];
  /** The spec-sheet key each binding constraint matched, by position (null when none did). */
  constraint_spec_keys?: Array<string | null>;
  spec_sheet_keys_unused?: string[];
  licensing_line?: string;
  licensing_note?: string | null;
  /** Markdown shown above the card (the what-if before/after lines). */
  intro?: string | null;
  /** The early ITAR answer of this run (S5), when it recorded one. */
  itar_screen?: CardItarScreen | null;
  /** What the second review changed since the Preliminary card (S6); with status `revised` when it passed. */
  revision?: string | null;
  /** Why it changed, in the second review's own plain sentence (S12 R-8); only with a revision. */
  revision_reason?: string | null;
  /** A Preliminary card whose second review sent it back: a corrected pass is running (S6). */
  rechecking?: boolean;
  /** What that review found, in a plain sentence (S12 R-8). */
  recheck_reason?: string | null;
  /** S10: the user's own words that put it here, and specs close to a limit. */
  evidence?: CardEvidence | null;
  near_misses?: CardNearMiss[];
}

const STATUSES: ResultCardStatus[] = ['preliminary', 'checked', 'provisional', 'revised', 'not_reviewed'];
const REGIMES: Regime[] = ['ITAR', 'EAR-listed', 'EAR99', 'uncertain'];

const isString = (value: unknown): value is string => typeof value === 'string';
const isObject = (value: unknown): value is Record<string, any> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const OUTCOMES: ItarScreenOutcome[] = ['on_usml', 'not_on_usml', 'unclear'];

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => isString(v) && Boolean(v.trim())) : [];

const optionalString = (value: unknown): string | null => (isString(value) && value.trim() ? value : null);

/** An ITAR screen from a frame, a card or a saved state; undefined when it isn't one. */
export const readItarScreen = (input: unknown): ItarScreen | undefined => {
  const raw = input as any;
  if (!isObject(raw) || !OUTCOMES.includes(raw.outcome)) return undefined;
  return {
    outcome: raw.outcome as ItarScreenOutcome,
    category: optionalString(raw.category),
    paragraph: optionalString(raw.paragraph),
    reasons: stringList(raw.reasons),
    closest_checked: stringList(raw.closest_checked),
    open_question: optionalString(raw.open_question),
  };
};

const readCardItarScreen = (input: unknown): CardItarScreen | null => {
  const screen = readItarScreen(input);
  if (!screen) return null;
  return { ...screen, revised: optionalString((input as any).revised) };
};

/** `evidence` from a frame or a stored chat: kept only when every quote really is in the source. */
const readEvidence = (raw: unknown): CardEvidence | null => {
  if (!isObject(raw) || !isString(raw.source) || !raw.source.trim()) return null;
  const quotes = stringList(raw.quotes).filter((quote) => raw.source.includes(quote));
  return quotes.length > 0 ? { source: raw.source, quotes: quotes } : null;
};

const readNearMisses = (raw: unknown): CardNearMiss[] =>
  (Array.isArray(raw) ? raw : [])
    .filter((n: any) => isObject(n) && isString(n.note) && n.note.trim() && isString(n.label))
    .map((n: any) => ({
      spec_key: isString(n.spec_key) ? n.spec_key : null,
      // The chat's own label for a sheet key, as for the spec rows below.
      label: isString(n.spec_key) ? humanizeKey(n.spec_key) : n.label,
      citation: isString(n.citation) ? n.citation : '',
      note: n.note,
    }));

const CONSTRAINT_TEXT_FIELDS = [
  'citation',
  'control_text',
  'control_type',
  'reason_for_control',
  'matched_spec',
  'explanation',
  'decontrol_threshold',
];
const CAVEAT_TEXT_FIELDS = ['missing_spec', 'assumed', 'impact'];
const CLASSIFICATION_TEXT_FIELDS = ['order_of_review_note', 'explanation', 'confidence'];

const isAbsent = (value: unknown): boolean => value === null || value === undefined;

/** Removes each of `fields` that holds something other than text (null and absent stay as they are). */
const dropNonText = (out: Record<string, any>, fields: string[]): void =>
  fields.forEach((field) => {
    if (!isAbsent(out[field]) && !isString(out[field])) delete out[field];
  });

/** The object entries of a list, each text field kept only when it is text and each list field cut to
 *  its strings: the card helpers call string and array methods on these. */
const readEntries = (raw: unknown, textFields: string[], listFields: string[] = []): Record<string, any>[] =>
  (Array.isArray(raw) ? raw : []).filter(isObject).map((entry) => {
    const out: Record<string, any> = { ...entry };
    dropNonText(out, textFields);
    listFields.forEach((field) => {
      if (!isAbsent(out[field])) out[field] = stringList(out[field]);
    });
    return out;
  });

/** A classification as the card reads it: odd nested values (a constraint list that isn't a list, a
 *  caveat that isn't an object, a number where text belongs) are dropped, not passed on to throw while
 *  rendering. The backend never sends these; a hand-edited or damaged stored chat might. */
const readClassification = (raw: Record<string, any>): ResultCardClassification => {
  const out: Record<string, any> = {
    ...raw,
    candidate_usml: stringList(raw.candidate_usml),
    candidate_eccns: stringList(raw.candidate_eccns),
    binding_constraints: readEntries(raw.binding_constraints, CONSTRAINT_TEXT_FIELDS, ['evidence_quotes']),
    caveats: readEntries(raw.caveats, CAVEAT_TEXT_FIELDS),
    near_misses: readEntries(raw.near_misses, ['spec', 'citation', 'note']),
  };
  dropNonText(out, CLASSIFICATION_TEXT_FIELDS);
  return out as ResultCardClassification;
};

const readVerifier = (raw: unknown): ResultCardVerifier | null =>
  isObject(raw)
    ? { ...raw, missing_specs: stringList(raw.missing_specs), issues: stringList(raw.issues) }
    : null;

/** A result card from a frame or a stored chat, or undefined when it isn't one. */
export const readResultCard = (raw: unknown): ResultCardPayload | undefined => {
  if (!isObject(raw) || !isObject(raw.classification)) return undefined;
  if (!isString(raw.classification.current_regime)) return undefined;
  const status = STATUSES.includes(raw.status) ? (raw.status as ResultCardStatus) : 'not_reviewed';
  return {
    ...(raw as ResultCardPayload),
    classification: readClassification(raw.classification),
    verifier: readVerifier(raw.verifier),
    itar_screen: readCardItarScreen(raw.itar_screen),
    revision: optionalString(raw.revision),
    revision_reason: optionalString(raw.revision) ? optionalString(raw.revision_reason) : null,
    rechecking: raw.rechecking === true,
    recheck_reason: raw.rechecking === true ? optionalString(raw.recheck_reason) : null,
    evidence: readEvidence(raw.evidence),
    near_misses: readNearMisses(raw.near_misses),
    status: status,
    specs: Array.isArray(raw.specs)
      ? raw.specs.filter((s: any) => isObject(s) && isString(s.key)).map((s: any) => ({
          key: s.key,
          // The chat's own label for a key (as on the Product Specs card), not the backend's: the two
          // humanizers differ on units ("Torque Range nm" vs "Torque Range (nm)").
          label: humanizeKey(s.key),
          value: isString(s.value) ? s.value : String(s.value ?? ''),
        }))
      : [],
  };
};

export const regimeOf = (card: ResultCardPayload): Regime => {
  const regime = card.classification.current_regime as Regime;
  return REGIMES.includes(regime) ? regime : 'uncertain';
};

// ------------------------------------------------------------------------- rebuilt from saved state

const normSpec = (text?: string | null): string =>
  (text || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Mirrors _spec_key_names: the key, its label, and the key without its trailing unit words (2+ words). */
const specKeyNames = (key: string): string[] => {
  const names = [normSpec(key), normSpec(humanizeKey(key))];
  let words = normSpec(key).split(' ');
  while (words.length > 2 && words[words.length - 1].length <= 3) {
    words = words.slice(0, -1);
    names.push(words.join(' '));
  }
  return names.filter(Boolean);
};

/** Mirrors _spec_key_for: the key itself or its label, else a phrase containing one of its names. */
export const specKeyFor = (matchedSpec: string | null | undefined, keys: string[]): string | null => {
  const target = normSpec(matchedSpec);
  if (!target) return null;
  const exact = keys.find((key) => specKeyNames(key).slice(0, 2).includes(target));
  if (exact) return exact;
  const within = keys.find((key) => specKeyNames(key).some((name) => target.includes(name)));
  return within || null;
};

// Mirrors _NOT_SPECS: facts the router sometimes also banks as specs.
const NOT_SPECS = ['product_name', 'product_type', 'product_description'];

/** Mirrors _result_card_status. */
const statusFromVerifier = (verifier?: ResultCardVerifier | null, revision?: string | null): ResultCardStatus => {
  if (!verifier) return 'not_reviewed';
  if (!verifier.supported) return 'provisional';
  return revision ? 'revised' : 'checked';
};

/** Mirrors _licensing_implication_body. */
export const licensingLineOf = (classification: ResultCardClassification): string => {
  const regime = classification.current_regime || 'uncertain';
  const posture = licencePostureOf(classification);
  if (posture) {
    return (
      `${posture} It is licensed by the U.S. Department of Commerce through the Bureau of Industry ` +
      'and Security (BIS), with applications filed in SNAP-R.'
    );
  }
  return LICENSING_IMPLICATION[regime] || LICENSING_IMPLICATION.uncertain;
};

/** Mirrors _licensing_destination_note. */
export const licensingNoteOf = (regime: string, state: Record<string, any>): string | null => {
  if (regime === 'uncertain') return null;
  const raw = state?.facts?.destination_country?.value;
  const country = isString(raw) ? raw.trim() : '';
  if (country) {
    if (state.licensing) return `The country-specific check for **${country}** is below.`;
    return (
      `You've given **${country}** as the destination — ask for a licensing check and I'll read the ` +
      'Country Chart against this classification.'
    );
  }
  return 'Add a destination country and I can advise on the specific licensing requirements for it.';
};

/** The USML category numeral in "XII", "Category XII(a)", "USML Category XII(a)(1) · SME". Mirrors _usml_roman. */
export const usmlRomanOf = (text?: string | null): string | null => {
  const match = /(?:\bcategory\s+|^\s*)([IVXL]+)\b/i.exec(text || '');
  return match ? match[1].toUpperCase() : null;
};

/** Mirrors _card_itar_screen: the state's ITAR screen next to the final classification, or null. */
export const cardItarScreenOf = (
  state: Record<string, any> | undefined | null,
  classification: ResultCardClassification,
): CardItarScreen | null => {
  const screen = readItarScreen(state?.itar_screen);
  const regime = classification.current_regime;
  if (!screen || !regime || regime === 'uncertain' || screen.outcome === 'unclear') return null;
  let revised: string | null = null;
  if (screen.outcome === 'not_on_usml' && regime === 'ITAR') {
    revised =
      "Revised: the early ITAR check found it wasn't on the US Munitions List; the full classification " +
      'found that it is.';
  } else if (screen.outcome === 'on_usml' && regime !== 'ITAR') {
    revised =
      "Revised: the early ITAR check pointed to the US Munitions List; the full classification found it isn't on it.";
  } else if (screen.outcome === 'on_usml') {
    const early = usmlRomanOf(screen.category);
    const final = Array.from(
      new Set(
        (classification.binding_constraints || [])
          .map((bc) => usmlRomanOf(bc?.citation))
          .filter((n): n is string => Boolean(n)),
      ),
    ).sort();
    if (early && final.length > 0 && !final.includes(early)) {
      revised = `Revised: the early ITAR check pointed to Category ${early}; the full classification placed it in Category ${final.join(', ')}.`;
    }
  }
  return { ...screen, revised: revised };
};

const EVIDENCE_MAX_QUOTES = 6;
const NEAR_MISS_MAX = 3;
const QUOTE_EDGES = /^["'“”‘’\s]+|["'“”‘’\s]+$/g;

/** Mirrors _card_evidence: the user message holding the most evidence quotes (earliest on a tie), and
 *  the quotes as they appear in it. */
export const evidenceFromState = (
  state: Record<string, any> | undefined | null,
  classification: ResultCardClassification,
): CardEvidence | null => {
  const quotes: string[] = [];
  (classification.binding_constraints || []).forEach((bc) => {
    stringList(bc?.evidence_quotes).forEach((raw) => {
      const quote = raw.replace(QUOTE_EDGES, '');
      if (quote.length >= 3 && !quotes.includes(quote)) quotes.push(quote);
    });
  });
  if (quotes.length === 0) return null;
  let best: CardEvidence | null = null;
  (Array.isArray(state?.conversation) ? state?.conversation : []).forEach((turn: any) => {
    const text = turn?.role === 'user' && isString(turn.text) && turn.text.trim() ? turn.text : null;
    if (!text) return;
    const lowered = text.toLowerCase();
    const found: string[] = [];
    quotes.forEach((quote) => {
      const at = lowered.indexOf(quote.toLowerCase());
      const span = at >= 0 ? text.slice(at, at + quote.length) : '';
      if (span && span.toLowerCase() === quote.toLowerCase() && !found.includes(span)) found.push(span);
    });
    if (found.length > 0 && (!best || found.length > best.quotes.length)) best = { source: text, quotes: found };
  });
  const chosen = best as CardEvidence | null;
  return chosen ? { source: chosen.source, quotes: chosen.quotes.slice(0, EVIDENCE_MAX_QUOTES) } : null;
};

/** Mirrors _card_near_misses. */
const nearMissesFromState = (classification: ResultCardClassification, keys: string[]): CardNearMiss[] =>
  (Array.isArray(classification.near_misses) ? classification.near_misses : [])
    .slice(0, NEAR_MISS_MAX)
    .filter((n) => isObject(n) && cardText(n.note))
    .map((n) => {
      const key = specKeyFor(n.spec, keys);
      return {
        spec_key: key,
        label: key ? humanizeKey(key) : humanizeSpecRef(cardText(n.spec)),
        citation: cardText(n.citation),
        note: cardText(n.note),
      };
    });

/** The card for the classification in `state`, or undefined when there is none. */
export const resultCardFromState = (
  state: Record<string, any> | undefined | null,
): ResultCardPayload | undefined => {
  const result = state?.classification?.result;
  if (!isObject(result) || !isString(result.current_regime)) return undefined;
  const classification = readClassification(result);
  const verifier = readVerifier(state?.classification?.verifier);
  const sheet: Record<string, any> = isObject(state?.spec_sheet) ? (state?.spec_sheet as any) : {};
  const keys = Object.keys(sheet);
  const constraintKeys = (classification.binding_constraints || []).map((bc) =>
    specKeyFor(bc?.matched_spec, keys),
  );
  const used = new Set(constraintKeys.filter(Boolean));
  const revision = optionalString(state?.classification?.revision);
  return {
    status: statusFromVerifier(verifier, revision),
    revision: revision,
    revision_reason: revision ? optionalString(state?.classification?.revision_reason) : null,
    classification: classification,
    verifier: verifier,
    specs: keys.map((key) => {
      const entry = sheet[key];
      const value = isObject(entry) ? entry.value : entry;
      return { key: key, label: humanizeKey(key), value: value === undefined || value === null ? '' : String(value) };
    }),
    constraint_spec_keys: constraintKeys,
    spec_sheet_keys_unused: keys.filter((key) => !used.has(key) && !NOT_SPECS.includes(key)),
    licensing_line: licensingLineOf(classification),
    licensing_note: licensingNoteOf(classification.current_regime || 'uncertain', state || {}),
    intro: null,
    itar_screen: cardItarScreenOf(state, classification),
    evidence: evidenceFromState(state, classification),
    near_misses: nearMissesFromState(classification, keys),
  };
};

// ------------------------------------------------------------------------- S10 view helpers

export interface EvidenceSegment {
  text: string;
  /** A quote: drawn highlighted. */
  hl?: boolean;
  /** Text left out of a long message. */
  gap?: boolean;
}

// A message longer than this is shown as excerpts around the highlights.
const EVIDENCE_FULL_MAX = 700;
const EVIDENCE_CONTEXT = 90;

/**
 * The user's message as segments, the quotes marked: only where they appear exactly (case and all),
 * so nothing the user didn't write can ever be highlighted. A long message keeps only the text around
 * the highlights. Empty when no quote is found.
 */
export const evidenceSegmentsOf = (evidence: CardEvidence | null | undefined): EvidenceSegment[] => {
  if (!evidence || !evidence.source) return [];
  const source = evidence.source;
  const ranges: Array<[number, number]> = [];
  evidence.quotes.forEach((quote) => {
    if (!quote) return;
    let at = source.indexOf(quote);
    while (at >= 0) {
      ranges.push([at, at + quote.length]);
      at = source.indexOf(quote, at + quote.length);
    }
  });
  if (ranges.length === 0) return [];
  ranges.sort((a, b) => a[0] - b[0]);
  const marks: Array<[number, number]> = [];
  ranges.forEach(([s, e]) => {
    const last = marks[marks.length - 1];
    if (last && s <= last[1]) last[1] = Math.max(last[1], e);
    else marks.push([s, e]);
  });
  // The stretches of the message shown: all of it, or windows around the marks.
  const windows: Array<[number, number]> = [];
  if (source.length <= EVIDENCE_FULL_MAX) {
    windows.push([0, source.length]);
  } else {
    marks.forEach(([s, e]) => {
      const from = Math.max(0, s - EVIDENCE_CONTEXT);
      const to = Math.min(source.length, e + EVIDENCE_CONTEXT);
      const last = windows[windows.length - 1];
      if (last && from <= last[1]) last[1] = Math.max(last[1], to);
      else windows.push([from, to]);
    });
  }
  const segments: EvidenceSegment[] = [];
  windows.forEach(([from, to], index) => {
    if (from > 0 || (index > 0 && from > windows[index - 1][1])) segments.push({ text: '…', gap: true });
    let at = from;
    marks
      .filter(([s, e]) => s >= from && e <= to)
      .forEach(([s, e]) => {
        if (s > at) segments.push({ text: source.slice(at, s) });
        segments.push({ text: source.slice(s, e), hl: true });
        at = e;
      });
    if (at < to) segments.push({ text: source.slice(at, to) });
  });
  if (windows.length > 0 && windows[windows.length - 1][1] < source.length) segments.push({ text: '…', gap: true });
  return segments;
};

/** The "didn't change the result" list with its near misses (board 04): each unused spec with the
 *  near miss that names it, then near misses for specs not on that list. */
export interface UnusedSpecRow {
  key: string;
  label: string;
  value: string;
  nearMiss?: string;
  /** Not one of the unused specs: a near miss on a spec the list doesn't hold. */
  nearOnly?: boolean;
}

export const unusedSpecRowsOf = (card: ResultCardPayload): { rows: UnusedSpecRow[]; nearMissCount: number } => {
  const nearMisses = card.near_misses || [];
  const unused = unusedSpecsOf(card);
  const rows: UnusedSpecRow[] = unused.map((spec) => {
    const near = nearMisses.find((n) => n.spec_key === spec.key);
    return { key: spec.key, label: spec.label, value: spec.value, nearMiss: near ? near.note : undefined };
  });
  nearMisses
    .filter((n) => !n.spec_key || !unused.some((spec) => spec.key === n.spec_key))
    .forEach((n, index) => {
      const value = n.spec_key ? (card.specs || []).find((s) => s.key === n.spec_key)?.value || '' : '';
      rows.push({
        key: `near-${index}-${n.spec_key || n.label}`,
        label: n.label,
        value: value,
        nearMiss: n.note,
        nearOnly: true,
      });
    });
  return { rows: rows, nearMissCount: nearMisses.length };
};

// -------------------------------------------------------------------------------------- view model

// Mirrors facts.CONTROL_TYPE_LABELS, for text that reaches the card without the backend's cleanup
// (a card rebuilt from a saved state).
const CONTROL_VOCAB_RE = /`\s*(parameter|nature_function|content_bom|end_use)\s*`|\b(nature_function|content_bom|end_use)\b/g;
const CONTROL_TYPE_LABELS: Record<string, string> = {
  parameter: 'performance threshold',
  nature_function: 'nature/function',
  content_bom: 'incorporated content',
  end_use: 'end-use',
};

/** Model text as the card shows it: stray markup and internal identifiers removed. */
export const cardText = (value?: string | null): string =>
  sanitizeModelText((value || '').replace(/\\n/g, '\n'))
    .replace(CONTROL_VOCAB_RE, (_m: string, a?: string, b?: string) => CONTROL_TYPE_LABELS[a || b || ''] || '')
    .trim();

// A parenthetical worth keeping in a starter: a short unit with no spaces ("(MHz)", "(mK)", "(Gy(Si)/s)").
const KEEP_PARENTHETICAL = /^\(\S{1,12}\)$/;
const STARTER_MAX = 100;

/** Drops each outermost "( … )" aside except a short unit; an unclosed "(" is left as written. */
const withoutAsides = (value: string): string => {
  let out = '';
  let depth = 0;
  let start = -1;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === '(') {
      if (depth === 0) start = i;
      depth += 1;
    } else if (ch === ')' && depth > 0) {
      depth -= 1;
      if (depth === 0) {
        const aside = value.slice(start, i + 1);
        out = KEEP_PARENTHETICAL.test(aside) ? out + aside : out.replace(/\s+$/, '');
      }
    } else if (depth === 0) {
      out += ch;
    }
  }
  return depth > 0 ? out + value.slice(start) : out;
};

/**
 * QA-2: the composer starter for "Tell me" on an assumption. The label is model text of any shape ("whether the
 * part is …", "the die length …", a whole paragraph), so no sentence is built around it: "<Short label>: ",
 * which reads for a noun phrase and a clause alike. Shortened to its first sentence without long asides.
 */
export const assumptionStarterOf = (label: string): string => {
  let short = withoutAsides(cardText(label)).replace(/\s+/g, ' ');
  const end = short.search(/[.?!](\s|$)/);
  if (end > 0) short = short.slice(0, end);
  short = short.replace(/[\s,;:.–—/-]+$/, '').trim();
  if (short.length > STARTER_MAX) {
    const cut = short.slice(0, STARTER_MAX);
    const space = cut.lastIndexOf(' ');
    const kept = (space > STARTER_MAX / 2 ? cut.slice(0, space) : cut)
      .replace(/(\s+(the|a|an|of|to|for|and|or|in|on|at|by|with|as|that|than))+$/i, '')
      .replace(/[\s,;:–—/-]+$/, '');
    short = `${kept}…`;
  }
  return short ? `${short.charAt(0).toUpperCase()}${short.slice(1)}: ` : '';
};

const usmlLabel = (primary: string): string =>
  /^\s*(usml|category)/i.test(primary) ? primary : `USML Category ${primary}`;

/** What the second review is, on the Preliminary card while it runs (S12 R-8, D-37). */
export const SECOND_REVIEW_NOTE =
  "A separate check is reading this result against your specs and the regulation text before it's final. It " +
  'usually takes under a minute, and most results pass unchanged.';

export const STATUS_LABELS: Record<ResultCardStatus, string> = {
  checked: 'Second review passed',
  provisional: 'Provisional',
  not_reviewed: "Second review didn't run",
  preliminary: 'Preliminary',
  revised: 'Revised',
};

export interface CardHeadline {
  title: string;
  code?: string;
  sub: string;
}

/** The title leads with the control; the sub-line says what it is controlled for and who licenses it. */
export const headlineOf = (card: ResultCardPayload): CardHeadline => {
  const regime = regimeOf(card);
  const primary = primaryControlOf(card.classification);
  if (regime === 'EAR-listed') {
    const reasons = controlReasonsOf(card.classification);
    return {
      title: 'EAR-listed:',
      code: primary ? `ECCN ${primary}` : undefined,
      sub: `${reasons.length ? `Controlled for ${reasonPhrase(reasons)}` : 'On the Commerce Control List'}. Commerce (BIS) licenses it.`,
    };
  }
  if (regime === 'ITAR') {
    return {
      title: 'ITAR:',
      code: primary ? usmlLabel(primary) : 'US Munitions List',
      sub: 'Controlled under the ITAR. State (DDTC) licenses it.',
    };
  }
  if (regime === 'EAR99') {
    return {
      title: 'EAR99',
      sub: 'Subject to the EAR, but not on the Commerce Control List or the US Munitions List.',
    };
  }
  return {
    title: 'Regime not settled yet',
    sub: 'Treat it as potentially controlled until the points below are confirmed.',
  };
};

/** "Category XII(a)(1)" from a screen's category and paragraph, or undefined. */
export const itarScreenPlaceOf = (screen: ItarScreen): string | undefined => {
  const numeral = usmlRomanOf(screen.category) || cardText(screen.category);
  if (!numeral) return undefined;
  const paragraph = cardText(screen.paragraph);
  return `Category ${numeral}${paragraph && !paragraph.startsWith('(') ? ' ' : ''}${paragraph}`;
};

export interface ItarScreenView {
  title: string;
  sub: string;
  /** The ITAR segment of the regime track, and whether the product sits there. */
  itarValue: string;
  onUsml: boolean;
  lane: string;
  /** The run panel's line beside the finished ITAR step. */
  stepMeta: string;
}

/** QA-6 (manager): the ITAR lane when it isn't on the USML, on the early card and the result card. */
export const ITAR_NOT_APPLICABLE = 'N/A Based on Current Specs';

/** The early card (board 03) for a screen: preliminary, before the EAR work and the second review. */
export const itarScreenViewOf = (screen: ItarScreen): ItarScreenView => {
  const place = itarScreenPlaceOf(screen);
  if (screen.outcome === 'on_usml') {
    return {
      title: `On the US Munitions List${place ? `: ${place}` : ''}.`,
      sub:
        'Items on the USML are controlled under the ITAR and licensed by State (DDTC), so the Commerce ' +
        "Control List doesn't apply. The result is being written up and checked now.",
      itarValue: place || 'On the list',
      onUsml: true,
      lane: 'Why it is on the USML',
      stepMeta: place ? `On the USML: ${place}` : 'On the USML',
    };
  }
  if (screen.outcome === 'not_on_usml') {
    return {
      title: 'Not ITAR. Checking the EAR next.',
      sub:
        "It isn't on the US Munitions List, so State (DDTC) doesn't control it. We're now testing your specs " +
        'against the Commerce Control List.',
      itarValue: ITAR_NOT_APPLICABLE,
      onUsml: false,
      lane: "Why it isn't ITAR",
      stepMeta: 'Not on the USML',
    };
  }
  return {
    title: 'ITAR not settled by the USML alone. Checking the EAR next.',
    sub: 'The full result will say where it lands once the Commerce Control List has been checked too.',
    itarValue: 'Not settled',
    onUsml: false,
    lane: 'What the USML review found',
    stepMeta: 'Not settled yet',
  };
};

export interface TrackSegment {
  id: 'itar' | 'ear-listed' | 'ear99';
  title: string;
  value: string;
  here: boolean;
}

/** Where the product sits: three fixed segments, the current one marked. */
export const trackOf = (card: ResultCardPayload): TrackSegment[] => {
  const regime = regimeOf(card);
  const primary = primaryControlOf(card.classification);
  const unsettled = regime === 'uncertain';
  return [
    {
      id: 'itar',
      title: 'ITAR (USML)',
      value: regime === 'ITAR' ? (primary ? usmlLabel(primary) : 'On the list') : unsettled ? 'Not settled' : ITAR_NOT_APPLICABLE,
      here: regime === 'ITAR',
    },
    {
      id: 'ear-listed',
      title: 'EAR-listed (CCL)',
      value:
        regime === 'EAR-listed'
          ? primary || 'On the list'
          : regime === 'ITAR'
          ? "Doesn't apply"
          : regime === 'EAR99'
          ? 'No entry matched'
          : 'Not settled',
      here: regime === 'EAR-listed',
    },
    {
      id: 'ear99',
      title: 'EAR99',
      value: regime === 'EAR99' ? 'Not on any list' : unsettled ? 'Not settled' : 'Not reached',
      here: regime === 'EAR99',
    },
  ];
};

export interface SpecRow {
  citation: string;
  reasons: string;
  name: string;
  value: string;
  valueIsText: boolean;
  why: string;
  clear: string;
  itar: boolean;
}

const isEccnCitation = (citation?: string | null): boolean => Boolean(eccnBase(citation));

const specRowFor = (
  bc: ResultCardConstraint,
  key: string | null,
  specs: ResultCardSpec[],
): SpecRow => {
  const spec = key ? specs.find((s) => s.key === key) : undefined;
  const matched = cardText(bc.matched_spec);
  const kind = bc.control_type || '';
  let name = 'Your spec';
  let value = matched;
  let valueIsText = true;
  if (spec) {
    name = spec.label;
    value = spec.value;
    valueIsText = false;
  } else if (!matched) {
    name = kind === 'end_use' ? 'Depends on' : 'Applies to';
    value = kind === 'end_use' ? 'How and where it is used' : 'The product as a whole';
  } else if (!/\s/.test(matched)) {
    value = humanizeSpecRef(matched);
  } else if (kind === 'nature_function') {
    // Controlled for what it is: the model describes the product rather than naming a spec.
    name = 'What it is';
  }
  return {
    citation: cardText(bc.citation) || 'Control',
    reasons: (bc.reason_for_control || '').trim(),
    name: name,
    value: value,
    valueIsText: valueIsText,
    why: cardText(bc.explanation) || cardText(bc.control_text),
    clear: cardText(bc.decontrol_threshold),
    itar: !isEccnCitation(bc.citation),
  };
};

/** The binding constraints as rows, the rows for one citation kept together (two-variant products). */
export const specRowsOf = (card: ResultCardPayload): SpecRow[] => {
  const constraints = card.classification.binding_constraints || [];
  const keys = card.constraint_spec_keys || [];
  const rows = constraints.map((bc, i) => specRowFor(bc || {}, keys[i] || null, card.specs || []));
  const order: string[] = [];
  rows.forEach((row) => {
    if (!order.includes(row.citation)) order.push(row.citation);
  });
  return order.reduce<SpecRow[]>(
    (grouped, citation) => grouped.concat(rows.filter((row) => row.citation === citation)),
    [],
  );
};

/** The specs on the sheet that no binding constraint matched. */
export const unusedSpecsOf = (card: ResultCardPayload): ResultCardSpec[] => {
  const unused = new Set(card.spec_sheet_keys_unused || []);
  return (card.specs || []).filter((spec) => unused.has(spec.key));
};

export interface AssumptionRow {
  label: string;
  text: string;
}

export const assumptionsOf = (card: ResultCardPayload): AssumptionRow[] =>
  (card.classification.caveats || [])
    .filter((c) => c && cardText(c.missing_spec))
    .map((c) => {
      const assumed = cardText(c.assumed).replace(/\.$/, '');
      const impact = cardText(c.impact);
      const label = humanizeSpecRef(cardText(c.missing_spec));
      return {
        label: label.charAt(0).toUpperCase() + label.slice(1),
        text: [assumed ? `Assumed ${assumed}.` : 'Not given.', impact].filter(Boolean).join(' '),
      };
    });

export interface TrailStep {
  step: string;
  text: string;
}

/** "How we got here": what each part of the review found, in order. */
export const trailOf = (card: ResultCardPayload): TrailStep[] => {
  const regime = regimeOf(card);
  const primary = primaryControlOf(card.classification);
  const usml =
    regime === 'ITAR'
      ? `Checked first. Listed${primary ? `: ${usmlLabel(primary)}` : ''}.`
      : regime === 'uncertain'
      ? 'Checked first. Not settled.'
      : 'Checked first. Not listed.';
  const ccl =
    regime === 'ITAR'
      ? "Doesn't apply: an item on the USML stays under ITAR."
      : regime === 'EAR-listed'
      ? `Matched${primary ? ` ECCN ${primary}` : ''}.`
      : regime === 'EAR99'
      ? 'No entry matched, so it falls to EAR99.'
      : 'Not settled.';
  const review: Record<ResultCardStatus, string> = {
    checked: 'Re-checked the citations and the specs they rely on. Supported.',
    provisional: 'Flagged concerns (shown above).',
    not_reviewed: "Didn't run.",
    preliminary: 'Under way.',
    revised: 'Changed the result.',
  };
  return [
    { step: '1. USML', text: usml },
    { step: '2. CCL', text: ccl },
    { step: '3. Second review', text: review[card.status] },
  ];
};
